import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Bell,
  Check,
  ChevronDown,
  CreditCard,
  HelpCircle,
  LogOut,
  PlayCircle,
  Plug,
  Search,
  User as UserIcon,
  Users,
} from 'lucide-react';

import { useAuth } from '../lib/auth/AuthProvider';
import { useHideBilling } from '../lib/billing';
import { isCompanyAdmin } from '../lib/team';
import { t } from '../lib/i18n';
import { cn } from '../lib/cn';
import { LanguageSwitch } from './LanguageSwitch';
import { ThemeToggle } from './ThemeToggle';
import { NotificationBell } from './NotificationBell';
import { SearchDialog } from './SearchDialog';
import { useHasFeature, FEATURE_CALENDAR_AGENDA } from '../lib/features';
import { AppMobileNav } from './AppMobileNav';
import { CompanySwitcher } from './CompanySwitcher';
import { Avatar } from './board/Avatar';
import { useTour } from './tour/ProductTour';

/** Link de navegación de la topbar con estado activo. */
function TopNavLink({ to, children, dataTour }: { to: string; children: ReactNode; dataTour?: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/inicio'}
      data-tour={dataTour}
      className={({ isActive }) =>
        cn(
          'rounded-token px-3 py-1.5 text-sm font-medium transition-colors',
          isActive ? 'bg-brand/10 text-brand' : 'text-muted hover:text-ink',
        )
      }
    >
      {children}
    </NavLink>
  );
}

/**
 * Barra superior de la app autenticada: logo, nombre de empresa, toggle de
 * tema y menú de usuario (con cerrar sesión).
 */
export function AppTopbar() {
  const { user, logout } = useAuth();
  const { startTour } = useTour();
  const [loggingOut, setLoggingOut] = useState(false);
  // Feedback transitorio tras cambiar de empresa ("Cambiaste a X"). La topbar
  // vive en el layout (persiste entre navegaciones) → buen lugar para el aviso.
  const [switchedTo, setSwitchedTo] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const hasAgenda = useHasFeature(FEATURE_CALENDAR_AGENDA);
  const canManageTeam = isCompanyAdmin(user?.role);
  // Empresa ACTIVE que no paga nada (monto 0) → ocultar "Facturación".
  const hideBilling = useHideBilling(canManageTeam);

  useEffect(() => {
    if (!switchedTo) return;
    const id = window.setTimeout(() => setSwitchedTo(null), 3500);
    return () => window.clearTimeout(id);
  }, [switchedTo]);

  // Atajo global de búsqueda: Ctrl/⌘ + K.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <header
      className="relative border-b border-line bg-surface"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Aviso transitorio de cambio de empresa (aria-live). */}
      {switchedTo && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex justify-center px-4"
        >
          <div className="hero-rise pointer-events-auto flex items-center gap-2 rounded-token border border-line bg-surface px-4 py-2.5 text-sm font-medium text-ink shadow-lift">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-good/15 text-good">
              <Check size={13} aria-hidden />
            </span>
            {t('company.switch.done', { name: switchedTo })}
          </div>
        </div>
      )}
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4">
        <div className="flex items-center gap-3 overflow-hidden">
          {/* Hamburguesa: sólo móvil (abre el drawer de navegación). */}
          <AppMobileNav onSwitched={setSwitchedTo} onOpenSearch={() => setSearchOpen(true)} />
          <Link to="/inicio" className="flex shrink-0 items-center gap-2">
            <img src={`${import.meta.env.BASE_URL}brand/logo-mark.png`} alt="" aria-hidden className="h-8 w-8" />
            <span className="hidden font-display font-semibold text-ink sm:inline">Tembiapo</span>
          </Link>
          {/* Nombre de empresa o switcher multi-empresa. */}
          <CompanySwitcher onSwitched={setSwitchedTo} />
        </div>

        <nav className="hidden items-center gap-1 sm:flex">
          <TopNavLink to="/inicio">{t('nav.home')}</TopNavLink>
          <TopNavLink to="/proyectos" dataTour="projects">
            {t('nav.projects')}
          </TopNavLink>
          <TopNavLink to="/mis-tareas">{t('nav.myTasks')}</TopNavLink>
          {hasAgenda && <TopNavLink to="/agenda">{t('nav.agenda')}</TopNavLink>}
          <TopNavLink to="/reportes">{t('nav.reports')}</TopNavLink>
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label={t('search.open')}
            title={t('search.open')}
            className="grid h-9 w-9 place-items-center rounded-token border border-line bg-surface-2 text-muted transition-colors hover:border-brand hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <Search size={18} />
          </button>
          <LanguageSwitch className="hidden sm:inline-flex" />
          <NotificationBell />
          {/* Tema y menú de usuario: sólo desktop; en móvil viven en el drawer. */}
          <span className="hidden sm:inline-flex">
            <ThemeToggle />
          </span>

          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                data-tour="user-menu"
                className="hidden items-center gap-2 rounded-token border border-line bg-surface-2 py-1 pl-1 pr-2 text-sm text-ink transition-colors hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:inline-flex"
              >
                {user ? (
                  <Avatar member={user} size="md" className="h-7 w-7" />
                ) : (
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-brand font-mono text-xs font-semibold text-brand-ink">
                    ?
                  </span>
                )}
                <span className="hidden max-w-[10rem] truncate sm:inline">
                  {user?.first_name} {user?.last_name}
                </span>
                <ChevronDown size={14} className="text-muted" />
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                className="z-50 min-w-[12rem] rounded-token border border-line bg-surface p-1 shadow-lg"
              >
                <div className="px-3 py-2">
                  <p className="truncate text-sm font-medium text-ink">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="truncate text-xs text-muted">{user?.email}</p>
                </div>
                <DropdownMenu.Separator className="my-1 h-px bg-line" />
                <DropdownMenu.Item
                  asChild
                  className="flex cursor-pointer items-center gap-2 rounded-[calc(var(--radius)-6px)] px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-brand"
                >
                  <Link to="/perfil">
                    <UserIcon size={15} />
                    {t('nav.profile')}
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  asChild
                  className="flex cursor-pointer items-center gap-2 rounded-[calc(var(--radius)-6px)] px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-brand"
                >
                  <Link to="/preferencias">
                    <Bell size={15} />
                    {t('nav.preferences')}
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  asChild
                  className="flex cursor-pointer items-center gap-2 rounded-[calc(var(--radius)-6px)] px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-brand"
                >
                  <Link to="/conectores">
                    <Plug size={15} />
                    {t('nav.connectors')}
                  </Link>
                </DropdownMenu.Item>
                {canManageTeam && (
                  <DropdownMenu.Item
                    asChild
                    className="flex cursor-pointer items-center gap-2 rounded-[calc(var(--radius)-6px)] px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-brand"
                  >
                    <Link to="/equipo">
                      <Users size={15} />
                      {t('nav.team')}
                    </Link>
                  </DropdownMenu.Item>
                )}
                {canManageTeam && !hideBilling && (
                  <DropdownMenu.Item
                    asChild
                    className="flex cursor-pointer items-center gap-2 rounded-[calc(var(--radius)-6px)] px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-brand"
                  >
                    <Link to="/facturacion">
                      <CreditCard size={15} />
                      {t('nav.billing')}
                    </Link>
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Separator className="my-1 h-px bg-line" />
                <DropdownMenu.Item
                  asChild
                  className="flex cursor-pointer items-center gap-2 rounded-[calc(var(--radius)-6px)] px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-brand"
                >
                  <Link to="/ayuda">
                    <HelpCircle size={15} />
                    {t('help.nav.help')}
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={(e) => {
                    e.preventDefault();
                    startTour();
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-[calc(var(--radius)-6px)] px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-brand"
                >
                  <PlayCircle size={15} />
                  {t('tour.replay')}
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="my-1 h-px bg-line" />
                <DropdownMenu.Item
                  onSelect={(e) => {
                    e.preventDefault();
                    void handleLogout();
                  }}
                  disabled={loggingOut}
                  className="flex cursor-pointer items-center gap-2 rounded-[calc(var(--radius)-6px)] px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-brand"
                >
                  <LogOut size={15} />
                  {t('home.userMenu.logout')}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>

      {/* Buscador global (proyectos + tareas). Se abre con el botón o Ctrl/⌘+K. */}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
